'use client';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from './AuthContext';
import type { Capacity } from '@/lib/capacity';

interface ProductionLite {
  id: string;
  title: string;
  stage: string;
  production_type: string;
  start_date: string | null;
  /** Per-production capacity for contractors — derived from the accepted
   *  offer on this production. null for staff (PM/admin/etc.) who see every
   *  production regardless of assignment. */
  capacity?: Capacity | null;
}

interface ActiveProductionContextValue {
  productions: ProductionLite[];
  loading: boolean;
  activeProductionId: string | null;
  activeProduction: ProductionLite | null;
  setActiveProductionId: (id: string | null) => void;
}

const STORAGE_KEY = 'active_production_id';

const ActiveProductionContext = createContext<ActiveProductionContextValue>({
  productions: [],
  loading: false,
  activeProductionId: null,
  activeProduction: null,
  setActiveProductionId: () => {},
});

export function useActiveProduction() {
  return useContext(ActiveProductionContext);
}

export function ActiveProductionProvider({ children }: { children: React.ReactNode }) {
  const { user, isContractor } = useAuth();
  const [productions, setProductions] = useState<ProductionLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeProductionId, setActiveProductionIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });

  // Load productions once the user is signed in.
  //
  // Staff (PM / admin / etc.) see every active production — they're not
  // assigned to specific shows. Contractors see only productions where they
  // hold an accepted offer; each entry comes with the capacity ("hat") that
  // offer represents, so the dashboard can route to the right body.
  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    // Every re-run (e.g. when isContractor flips from false→true once the
    // profile resolves) needs to mark loading=true so consumers don't
    // briefly see the previous run's empty result and over-react.
    setLoading(true);
    const supabase = createClient();

    if (!isContractor) {
      supabase
        .from('productions')
        .select('id, title, stage, production_type, start_date')
        .neq('stage', 'wrapped')
        .order('start_date', { ascending: true, nullsFirst: false })
        .then(({ data }: { data: ProductionLite[] | null }) => {
          setProductions((data ?? []) as ProductionLite[]);
          setLoading(false);
        });
      return;
    }

    // Contractor: scope to productions where they hold an accepted offer.
    // capacity is now persisted directly on the offer row (stamped by a
    // BEFORE INSERT/UPDATE trigger from production_roles.department +
    // role_name) so we no longer need to join + derive at read time.
    (async () => {
      const { data: contractor } = await supabase
        .from('contractors')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!contractor?.id) { setProductions([]); setLoading(false); return; }

      const { data: offers } = await supabase
        .from('offers')
        .select(`
          production_id,
          capacity,
          updated_at,
          productions:production_id ( id, title, stage, production_type, start_date )
        `)
        .eq('contractor_id', contractor.id)
        .eq('offer_status', 'accepted')
        .order('updated_at', { ascending: false });

      // Deduplicate by production (most-recent accepted offer wins) and
      // skip productions that are already wrapped.
      const seen = new Set<string>();
      const list: ProductionLite[] = [];
      for (const o of (offers ?? []) as any[]) {
        const p = o.productions;
        if (!p || seen.has(p.id) || p.stage === 'wrapped') continue;
        seen.add(p.id);
        list.push({
          id: p.id,
          title: p.title,
          stage: p.stage,
          production_type: p.production_type,
          start_date: p.start_date,
          capacity: o.capacity ?? null,
        });
      }
      list.sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''));
      setProductions(list);
      setLoading(false);
    })();
  }, [user?.id, isContractor]);

  const setActiveProductionId = useCallback((id: string | null) => {
    setActiveProductionIdState(id);
    if (typeof window !== 'undefined') {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // If the saved production no longer exists in the list, clear it
  useEffect(() => {
    if (loading || !activeProductionId) return;
    if (productions.length > 0 && !productions.find((p) => p.id === activeProductionId)) {
      setActiveProductionId(null);
    }
  }, [productions, activeProductionId, loading, setActiveProductionId]);

  const activeProduction = productions.find((p) => p.id === activeProductionId) ?? null;

  return (
    <ActiveProductionContext.Provider data-component-id="src/contexts/ActiveProductionContext.tsx:143:4" data-component-path="src/contexts/ActiveProductionContext.tsx" data-component-line="143" data-component-end-line="153" data-component-file="ActiveProductionContext.tsx" data-component-name="ActiveProductionContext.Provider" data-component-content="%7B%22elementName%22%3A%22ActiveProductionContext.Provider%22%7D"
      value={{
        productions,
        loading,
        activeProductionId,
        activeProduction,
        setActiveProductionId,
      }}
    >
      {children}
    </ActiveProductionContext.Provider>
  );
}
